import type { ReactNode } from 'react';
import React, { useContext } from 'react';

import { localeDetails } from '~/components/CmsMain/localeGlobals';

const CurrencyFormatterContext = React.createContext<CurrencyFormatterType>({
  currencyFormatter: { format: (num: number) => `${num}` },
});

const CurrencyFormatterContextProvider: React.FC<{ currency?: string; children: ReactNode }> = ({
  currency,
  children,
}) => {
  const details = localeDetails ? localeDetails : { locale: 'en-us', currency: '' };
  const defaultCurrency = currency || details.currency;

  const currencyFormatter = new Intl.NumberFormat(details.locale, {
    style: 'currency',
    currency: defaultCurrency,
  });

  const getLocaleSeparator = (separatorType?: string) => {
    const numberWithGroupAndDecimalSeparator = 1000.1;
    return currencyFormatter
      .formatToParts(numberWithGroupAndDecimalSeparator)
      .find((part) => part.type === separatorType)?.value;
  };

  // Enhanced format function that accepts optional currency override
  const format = (amount: number, currencyOverride?: string): string => {
    if (!currencyOverride || currencyOverride === defaultCurrency) {
      return currencyFormatter.format(amount);
    }

    // Create formatter with override currency
    const overrideFormatter = new Intl.NumberFormat(details.locale, {
      style: 'currency',
      currency: currencyOverride,
    });
    return overrideFormatter.format(amount);
  };

  // Create enhanced currencyFormatter object with both original format and new format method
  const enhancedCurrencyFormatter = {
    ...currencyFormatter,
    format,
  };

  return (
    <CurrencyFormatterContext.Provider
      value={{
        currencyFormatter: enhancedCurrencyFormatter,
        groupSeparator: getLocaleSeparator('group'),
        decimalSeparator: getLocaleSeparator('decimal'),
        symbol: getLocaleSeparator('currency'),
      }}
    >
      {children}
    </CurrencyFormatterContext.Provider>
  );
};

const useCurrencyFormatter = (): CurrencyFormatterType => useContext(CurrencyFormatterContext);

export type CurrencyFormatterType = {
  currencyFormatter: { format: (num: number, currencyOverride?: string) => string };
  decimalSeparator?: string;
  groupSeparator?: string;
  symbol?: string;
};

export default useCurrencyFormatter;
export { CurrencyFormatterContextProvider, useCurrencyFormatter };
